import React, { useState } from "react";
import { MessageCircle, Zap, ShieldCheck, CheckCircle2, PauseCircle, Ban } from "lucide-react";

const CHECKS = ["User permission", "Action type", "Policy", "Customer context", "Sensitive information", "Required approval"];

const OUTCOMES = {
  permitted: { icon: CheckCircle2, label: "Permitted", tone: "green", body: "Within policy — the action executes and is recorded." },
  controlled: { icon: PauseCircle, label: "Controlled", tone: "amber", body: "Held for human authorisation before it can proceed." },
  prohibited: { icon: Ban, label: "Prohibited", tone: "red", body: "Out of policy — blocked and an incident is raised." },
};

const toneMap = {
  green: "border-signal-green/40 bg-signal-green/[0.06] text-signal-green",
  amber: "border-signal-amber/40 bg-signal-amber/[0.06] text-signal-amber",
  red: "border-signal-red/40 bg-signal-red/[0.06] text-signal-red",
};

export default function AgenticControl() {
  const [outcome, setOutcome] = useState("controlled");
  const cur = OUTCOMES[outcome];
  const OIcon = cur.icon;

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="fs-agentic">
      <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-white/10 bg-navy-800/50 p-4">
            <MessageCircle className="h-5 w-5 text-muted-foreground" />
            <p className="mt-2.5 text-sm font-semibold text-foreground">AI that answers</p>
            <p className="mt-1 text-xs text-muted-foreground">Responds with information.</p>
          </div>
          <div className="rounded-xl border border-primary/30 bg-primary/[0.06] p-4">
            <Zap className="h-5 w-5 text-primary" />
            <p className="mt-2.5 text-sm font-semibold text-foreground">AI that acts</p>
            <p className="mt-1 text-xs text-muted-foreground">Takes an action with real effect.</p>
          </div>
        </div>
        <p className="mt-5 text-sm text-muted-foreground">Synthetic example: <span className="text-foreground">an AI agent proposes sending a customer communication.</span> Before it acts, Aegis checks:</p>
        <div className="mt-4 grid grid-cols-2 gap-2">
          {CHECKS.map((c) => (
            <div key={c} className="flex items-center gap-2 rounded-lg border border-white/10 bg-navy-800/50 px-3 py-2 text-sm text-foreground/90"><ShieldCheck className="h-4 w-4 text-primary" /> {c}</div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6">
        <p className="text-sm font-semibold text-foreground">Outcome</p>
        <div className="mt-3 flex gap-2">
          {Object.keys(OUTCOMES).map((k) => (
            <button key={k} onClick={() => setOutcome(k)} data-testid={`fs-agentic-${k}`}
              className={`flex-1 rounded-lg border px-3 py-2 text-xs font-medium capitalize transition-colors ${outcome === k ? toneMap[OUTCOMES[k].tone] : "border-white/10 bg-navy-800/50 text-muted-foreground hover:border-white/20"}`}>
              {OUTCOMES[k].label}
            </button>
          ))}
        </div>
        <div className={`mt-4 rounded-xl border p-6 ${toneMap[cur.tone]}`}>
          <OIcon className="h-8 w-8" />
          <p className="mt-3 text-lg font-bold text-foreground">{cur.label}</p>
          <p className="mt-1 text-sm text-foreground/85">{cur.body}</p>
        </div>
        <p className="mt-4 text-xs text-muted-foreground/70">norse3 is not approved to autonomously execute regulated financial decisions. High-impact actions are held for authorised people.</p>
      </div>
    </div>
  );
}
